import { useMemo } from 'react';
import { useSolicitationInsights } from '../../../../hooks/useSolicitationInsights';
const EMPTY = {
    pipelineVolume: undefined,
    pageLimits: [],
    formatRules: [],
    contentRequirements: [],
    fileRequirements: []
};
// CROSS_VOLUME_TAGS are sentinel values the LLM uses when a fact applies
// uniformly across every volume (e.g. "all volumes shall use 12-point
// Arial"). Page limits and *typed* format rules still broadcast because
// a project-wide page cap and a project-wide font/margin/spacing rule
// are both genuinely useful per-volume context.
//
// Cross-volume content / file requirements DO NOT broadcast — those are
// volume-specific by nature and the same fact rendering on all 6 volume
// pages was confusing users (Volume VI's SF1449 etc.). They still
// surface in the global SolicitationInsightsPanel.
const CROSS_VOLUME_TAGS = new Set([
    'all volumes',
    'all volume',
    'every volume',
    'any volume',
    'all'
]);
// BROADCASTABLE_FORMAT_RULE_TYPES are the format-rule kinds that are
// genuinely document-wide constraints (font, margin, spacing, page
// geometry) and SHOULD broadcast cross-volume when tagged "All Volumes".
//
// PR-B.7.8a: ruleType "other" is excluded because the LLM uses it as
// a catch-all for pricing-specific instructions ("use the format
// depicted in the Pricing Spreadsheet", "two decimal places for labor
// rates") that get mis-tagged as "All Volumes" and leak onto every
// per-volume page. Pricing rules with type "other" still appear on the
// cost volume's page via strict name matching once the prompt rewrite
// (PR-B.7.8d) tags them with the cost volume name.
const BROADCASTABLE_FORMAT_RULE_TYPES = new Set([
    'font',
    'font_size',
    'line_spacing',
    'margin',
    'page_size',
    'page_orientation',
    'header',
    'footer',
    'page_numbering',
    'color'
]);
export function useVolumeMetadata(projectId, volume) {
    const insights = useSolicitationInsights(projectId ?? '');
    const pipeline = insights.data?.pipeline;
    return useMemo(()=>{
        if (!volume || !pipeline) return EMPTY;
        // Join the canonical volume — try identifier match first, fall back to name.
        const pipelineVolume = (pipeline.volumes ?? []).find((v)=>volume.originalIdentifier && v.identifier === volume.originalIdentifier || v.name === volume.displayName || v.identifier === volume.displayName) ?? undefined;
        // Per-fact arrays use volumeName. The insights pipeline tags facts with
        // the canonical volume name from Stage 1, which usually matches one of
        // pipelineVolume.name, pipelineVolume.identifier, the project volume's
        // displayName, or originalIdentifier.
        //
        // Match strategy: case-insensitive EXACT match against the canonical
        // candidate set, plus a special case for cross-cutting volumeName tags
        // ("All Volumes", "Any Volume", etc.) which apply to every volume.
        //
        // Substring fallback is unsafe with Roman-numeral identifiers —
        // "Volume I" is a substring of "Volume II" / "Volume III" / "Volume IV",
        // which would cause facts from later volumes to bubble up into Volume I.
        const candidateNames = new Set([
            volume.displayName,
            volume.originalIdentifier,
            pipelineVolume?.name,
            pipelineVolume?.identifier
        ].filter((s)=>!!s).map((s)=>s.trim().toLowerCase()));
        // matchesVolumeStrict — exact match against this volume's canonical
        // names only. Cross-volume sentinels do NOT match. Used for content
        // and file requirements so per-volume pages show only their own
        // specific reqs.
        const matchesVolumeStrict = (volumeName)=>{
            if (!volumeName) return false;
            const normalized = volumeName.trim().toLowerCase();
            return candidateNames.has(normalized);
        };
        // matchesVolumeOrCrossVolume — strict match plus cross-volume
        // sentinels ("all volumes", etc). Used for format rules and page
        // limits where genuinely-global facts ARE useful per-volume context.
        const matchesVolumeOrCrossVolume = (volumeName)=>{
            if (matchesVolumeStrict(volumeName)) return true;
            if (!volumeName) return false;
            return CROSS_VOLUME_TAGS.has(volumeName.trim().toLowerCase());
        };
        // PR-C.3 + PR-B.7.1: prefer current-version rows when the merge step
        // has stamped them. Without this filter, the per-volume metadata
        // panel would show every amendment version of the same fact.
        const onlyCurrent = (rows)=>{
            if (!rows) return [];
            const filtered = rows.filter((r)=>r.isCurrentVersion !== false);
            // If nothing has the flag set (legacy data), fall back to all rows.
            if (filtered.length === 0 && rows.length > 0 && !rows.some((r)=>r.isCurrentVersion === true)) {
                return rows;
            }
            return filtered;
        };
        return {
            pipelineVolume,
            pageLimits: onlyCurrent(pipeline.pageLimits).filter((p)=>matchesVolumeOrCrossVolume(p.volumeName)),
            formatRules: onlyCurrent(pipeline.formatRules).filter((r)=>{
                // Strict name match always wins.
                if (matchesVolumeStrict(r.volumeName)) return true;
                // Cross-volume sentinel: only broadcast typed document
                // formatting rules (font, margin, spacing, page geometry).
                // Type "other" / unknown is the bucket where pricing-specific
                // rules get mis-tagged "All Volumes", so don't broadcast those.
                if (!r.volumeName) return false;
                const isCross = CROSS_VOLUME_TAGS.has(r.volumeName.trim().toLowerCase());
                if (!isCross) return false;
                const ruleType = (r.ruleType || '').trim().toLowerCase();
                return BROADCASTABLE_FORMAT_RULE_TYPES.has(ruleType);
            }),
            contentRequirements: onlyCurrent(pipeline.contentRequirements).filter((c)=>matchesVolumeStrict(c.volumeName)),
            fileRequirements: onlyCurrent(pipeline.fileRequirements).filter((f)=>matchesVolumeStrict(f.volumeName))
        };
    }, [
        volume,
        pipeline
    ]);
}
